import { useState, useEffect } from 'react';
import { ThemeContext } from '.';

// Helper functions for localStorage
const getStoredTheme = () => {
  const stored = localStorage.getItem('tabularjsontheme');
  if (stored === 'dark' || stored === 'light') {
    return stored;
  }
  // Check system preference if no stored value
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
};

const getStoredThemeType = (theme) => {
  // Store theme type separately for dark and light modes
  const key = `tabularjsonthemetype-${theme}`;
  return localStorage.getItem(key) || 'default';
};

export const ThemeProvider = ({ children }) => {
  const initialTheme = getStoredTheme();
  const [theme, setTheme] = useState(initialTheme);
  const [themeType, setThemeType] = useState(getStoredThemeType(initialTheme));

  // Initialize theme on mount
  useEffect(() => {
    // Apply theme classes to body
    if (theme === 'light') {
      document.body.classList.add('light-mode');
    } else {
      document.body.classList.remove('light-mode');
    }

    // Apply theme type
    if (themeType !== 'default') {
      document.body.classList.add(themeType);
    }

    document.body.classList.remove('preload');
  }, [theme, themeType]);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    const newThemeType = getStoredThemeType(newTheme);

    if (newTheme === 'dark') {
      document.body.classList.remove('light-mode');
    } else {
      document.body.classList.add('light-mode');
    }

    // Remove old theme type class
    if (themeType !== 'default') {
      document.body.classList.remove(themeType);
    }

    // Add new theme type class
    if (newThemeType !== 'default') {
      document.body.classList.add(newThemeType);
    }

    document.body.classList.remove('preload');

    setTheme(newTheme);
    setThemeType(newThemeType);

    // Save only the theme (light/dark), not the theme type
    localStorage.setItem('tabularjsontheme', newTheme);
  };

  const setThemeTypeHandler = (type) => {
    setThemeType((prevState) => {
      // Remove old theme type class
      if (prevState !== 'default') {
        document.body.classList.remove(prevState);
      }

      // Add new theme type class
      if (type !== 'default') {
        document.body.classList.add(type);
      }

      // Save to localStorage with theme-specific key
      const key = `tabularjsonthemetype-${theme}`;
      localStorage.setItem(key, type);

      return type;
    });
  };

  return (
    <ThemeContext.Provider
      value={{ theme, toggleTheme, themeType, setThemeTypeHandler }}
    >
      {children}
    </ThemeContext.Provider>
  );
};
